import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { MBPError } from '@errors/mbp-error';
import { environment } from '@environments/environment';

// Minimum time that must elapse between two automatic re-authentication
// redirects. A 401 that arrives immediately after a successful login means the
// freshly-issued token is being rejected by the API; redirecting to the
// identity provider (which still holds an active session) would bounce the user
// straight back and hit the same 401 again, looping forever. Allowing at most
// one redirect per window breaks that cycle while still recovering from a
// genuinely expired token later in the session.
const REAUTH_MIN_INTERVAL_MS = 30_000;
const REAUTH_TIMESTAMP_KEY = 'mbp_last_reauth_at';

function canTriggerReauth(): boolean {
  const now = Date.now();
  const last = Number(sessionStorage.getItem(REAUTH_TIMESTAMP_KEY) ?? '0');
  if (now - last < REAUTH_MIN_INTERVAL_MS) {
    return false;
  }
  sessionStorage.setItem(REAUTH_TIMESTAMP_KEY, String(now));
  return true;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Let the OIDC library manage its own requests (token endpoint, userinfo, discovery).
  // Intercepting these can overwrite the library's own Authorization header with a
  // stale/empty token, causing 401s that then trigger a broken re-auth cycle.
  if (req.url.startsWith(environment.authConfig.authority)) {
    return next(req);
  }

  if (req.url.startsWith(environment.excelExportUrl)) {
    return next(req);
  }

  return authService.getAccessToken().pipe(
    switchMap((token) => {
      const authReq = token
        ? req.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          })
        : req;

      return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            // Token rejected by server — redirect to the IdP for re-auth, but
            // only if we are not already in a redirect loop (see comment above).
            const willReauth = canTriggerReauth();
            if (willReauth) {
              authService.login();
            }
            const mbpError: MBPError = {
              statusCode: 401,
              handled: true,
              // Surface the failure for diagnostics when we deliberately stop
              // looping instead of redirecting again.
              logError: !willReauth,
              originalError: error
            };
            return throwError(() => mbpError);
          } else if (error.status === 403) {
            const mbpError: MBPError = {
              statusCode: 403,
              handled: false,
              logError: true,
              originalError: error
            };
            return throwError(() => mbpError);
          }
          return throwError(() => error);
        })
      );
    })
  );
};
